import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FlaskConical, Calculator, Globe, Languages, Dumbbell, Palette, BookMarked, GraduationCap, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = {
  math: Calculator, science: FlaskConical, humanities: Globe,
  language: Languages, phe: Dumbbell, arts: Palette,
};
const ACCENT = {
  math: "bg-indigo-100 text-indigo-600",
  science: "bg-sky-100 text-sky-600",
  humanities: "bg-orange-100 text-orange-600",
  language: "bg-purple-100 text-purple-600",
  phe: "bg-rose-100 text-rose-600",
  arts: "bg-pink-100 text-pink-600",
};

// map a curriculum subject to exam subjects that count as related
const relatesTo = (subjectName, examSubject) => {
  if (examSubject === "All Subjects") return true;
  if (examSubject === subjectName) return true;
  // handle "Mathematics (Standard & Extended)" vs exam subject "Mathematics"
  return subjectName.startsWith(examSubject);
};

export default function Curriculum({ askTutor }) {
  const [subjects, setSubjects] = useState([]);
  const [exams, setExams] = useState([]);

  useEffect(() => {
    api.get("/curriculum").then((r) => setSubjects(r.data));
    api.get("/exams").then((r) => setExams(r.data));
  }, []);

  const today = new Date(new Date().toDateString());
  const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });

  return (
    <div>
      <p className="text-sm text-slate-500 mb-5">Grade 9 MYP subject roadmap for 2026–27. Tap a unit to ask the AI Tutor about it.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="curriculum-grid">
        {subjects.map((s, i) => {
          const Icon = ICONS[s.icon] || BookMarked;
          const subjectExams = exams
            .filter((e) => relatesTo(s.subject, e.subject) && new Date(e.date) >= today)
            .sort((a, b) => new Date(a.date) - new Date(b.date));
          return (
            <motion.div key={s.subject} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${ACCENT[s.icon] || "bg-slate-100 text-slate-600"}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-slate-900">{s.subject}</h3>
                </div>
                {subjectExams.length > 0 && (
                  <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-600 flex items-center gap-1">
                    <GraduationCap className="w-3 h-3" /> {subjectExams.length}
                  </span>
                )}
              </div>

              <div className="space-y-3">
                {s.units.map((u, idx) => (
                  <div key={idx} className="relative pl-5 border-l-2 border-slate-100 group">
                    <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-indigo-500 ring-4 ring-indigo-50" />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold uppercase tracking-wide text-indigo-500">{u.unit}</span>
                      {u.duration && <span className="text-[11px] text-slate-400">{u.duration}</span>}
                    </div>
                    <p className="text-sm font-medium text-slate-800 mt-0.5">{u.title}</p>
                    {u.concepts && <p className="text-xs text-slate-500 mt-0.5">{u.concepts}</p>}
                    {u.criteria && <p className="text-[11px] text-slate-400 mt-0.5">Assessed: Criteria {u.criteria}</p>}
                    <button
                      data-testid={`ask-tutor-${s.icon}-${idx}`}
                      onClick={() => askTutor(`Explain the Grade 9 MYP ${s.subject} unit "${u.title}" (${u.concepts || ""}). Break it down simply with examples and tell me the key things I should revise.`)}
                      className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-purple-600 hover:text-purple-700 transition-colors"
                    >
                      <Sparkles className="w-3 h-3" /> Ask AI Tutor
                    </button>
                  </div>
                ))}
              </div>

              {subjectExams.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">Related assessments</p>
                  <div className="space-y-1.5">
                    {subjectExams.slice(0, 3).map((e) => (
                      <div key={e.id} className="flex items-center gap-2 text-xs">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                        <span className="text-slate-600 flex-1 truncate">{e.topic || e.subject}</span>
                        <span className="text-slate-400 font-medium">{fmtDate(e.date)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
